import Footer from 'components/footer';
import {
  CitationBlock,
  CitationBlockMini,
  CitationExampleBox,
  GuideHeader,
  GuideSection,
  GuideTitle,
  GuideWrapper,
  IntextCitation,
  SingleSpacedList,
  TextWithColor,
} from 'components/guide';
import GuideHero from 'components/guide-hero';
import GuideLinkButton, {
  GuideLinkButtonSection,
} from 'components/guide-link-button';

const VancouverGuide: React.FC = () => (
  <>
    <GuideHero
      header="Our Complete Guide to Citing in Vancouver Format"
      imgSrc={require('images/vancouver-modal.png')}
      imgAlt={'Laptop with screenshot on screen of manual for Vancouver style'}
    >
      <p>
        In this user-friendly guide, we&apos;re going to demystify the Vancouver
        citation style. You&apos;ll discover how to format in-text citations and
        compile an authoritative reference list, without breaking a sweat.
      </p>
      <p>
        Ready to master Vancouver citations? See how our citation generator can
        take the guesswork out of your referencing, and understand how to get
        the Vancouver format spot-on, every single time.
      </p>
      <p>
        And don&apos;t worry, creating a Vancouver bibliography isn&apos;t as
        complex as it sounds!
      </p>
    </GuideHero>
    <GuideWrapper>
      <GuideTitle>Discover More Bibcitation Guides</GuideTitle>
      <GuideLinkButtonSection>
        <GuideLinkButton text="MLA Guide" href="/mla" variant="purple" />
        <GuideLinkButton text="APA Guide" href="/apa" variant="blue" />
        <GuideLinkButton
          text="Chicago Guide"
          href="/chicago"
          variant="orange"
        />
      </GuideLinkButtonSection>
      <FormatOverview />
      <InText />
      <Bibliography />
      <BookCitations />
      <JournalCitations />
      <WebsiteCitations />
      <BookChapterCitations />
      <NewspaperAndMagazineCitations />
      <ConferencePaperCitations />
      <SocialMediaCitations />
      <ThesisDissertationsCitations />
      <DataCitations />
      <DictionariesAndEncyclopediaCitations />
      <DrugCitations />
      <EvidenceSummaryCitations />
      <StandardsAndPharmacopoeiaCitations />
      <TablesAndFiguresCitations />
      <TechnicalReportsCitations />
      <CourseMaterialCitations />
      <PersonalCommunicationsCitations />
      <FinalNotes />
    </GuideWrapper>
    <Footer />
  </>
);

const FormatOverview: React.FC = () => (
  <GuideSection id="Overview">
    <GuideHeader>Vancouver Style Overview</GuideHeader>
    <p>
      <strong>The Vancouver Referencing Style</strong>, established by the
      International Committee of Medical Journal Editors (ICMJE), is frequently
      used in the medical and scientific communities. The system is
      characterized by the use of numbers for citations, offering a concise and
      practical way to cite sources in your scholarly work.
    </p>
    <p>
      This guide will provide you with the essentials of using the Vancouver
      style to acknowledge the contributions of other authors and uphold
      academic integrity.
    </p>
    <p>
      In the Vancouver style, there are two key components of citations: in-text
      citations and the reference list.
    </p>
    <p>
      <strong>In-text citations</strong> in the Vancouver style are typically
      represented as numbers either in brackets, parentheses, or as superscripts
      within the body of your work. These numbers correspond to the sources
      listed in the reference list. When you quote or paraphrase a source, you
      indicate its number.
    </p>
    <p>
      <strong>The reference list</strong>, also called a bibliography in
      Vancouver style, presents full details of all sources cited within your
      work. The list is compiled in the order in which the sources appear in
      your text. The structure of each entry can vary based on the type of
      source.
    </p>
    <p>
      At Bibcitation, we understand that the Vancouver style, with its unique
      numbering system and detailed references, can be challenging to master.
      That&apos;s why we&apos;ve developed an intuitive Vancouver citation
      generator to assist you. Simply provide the necessary information, and
      we&apos;ll deliver a perfectly formatted citation, whether it&apos;s for a
      book, a journal article, a webpage, or another type of source.
    </p>
  </GuideSection>
);

const InText: React.FC = () => (
  <GuideSection id="InText">
    <GuideHeader>Create In-text Citations in Vancouver</GuideHeader>
    <p>
      In the Vancouver Referencing Style, in-text citations are numerically
      represented within your paper&apos;s text, guiding readers to the
      corresponding full citation in the reference list. Each source you cite
      gets a unique number depending on its first appearance in the text.
    </p>
    <p>
      Let&apos;s explore some examples of in-text citations using the Vancouver
      style:
    </p>

    <p>
      <strong>Standard citation:</strong> The most typical form of citation
      includes a number in brackets or parentheses at the end of the related
      sentence. For instance:{' '}
      <IntextCitation>The research results were significant (1)</IntextCitation>
      .
    </p>
    <p>
      <strong>Several sources at once:</strong> When referencing multiple
      sources at once, you would list each number consecutively in the order you
      mention them, separated by commas within one set of parentheses. For
      example:{' '}
      <IntextCitation>
        Several studies have explored this phenomenon (1,2,3)
      </IntextCitation>
      .
    </p>
    <p>
      <strong>Subsequent citation of a source:</strong> If you cite the same
      source again later in your work, you reuse its original number. For
      instance:{' '}
      <IntextCitation>
        As noted earlier, the research findings were significant (1)
      </IntextCitation>
      .
    </p>
    <p>
      <strong>Direct quotations:</strong> When you include a direct quote from a
      source, you would append the specific page number after the citation
      number like so: According to Brown,
      <IntextCitation>
        &quot;This was an unexpected finding&quot; (2, p.157)
      </IntextCitation>
      .
    </p>
    <p>
      <strong>Multiple authors:</strong> The number of authors does not affect
      the in-text citation in Vancouver style. Whether a source has one author
      or multiple, the in-text citation remains a number.
    </p>
    <p>
      <strong>Referencing sources within sources:</strong> If you&apos;re citing
      a source you found in another source, you should, ideally, locate the
      original source and cite that. However, if this isn&apos;t possible, you
      can use the number for the indirect source you have.
    </p>

    <p>
      For instance:{' '}
      <IntextCitation>
        The findings of this study have been contradicted in more recent
        research (1 cited in 2)
      </IntextCitation>
      .
    </p>
    <p>
      <strong>Long quotations:</strong> For extended quotations in Vancouver
      style, use an indented paragraph without quotation marks. You should place
      the in-text citation after the final punctuation.
    </p>
    <p>
      Example:{' '}
      <IntextCitation>
        The significance of accurate referencing has been aptly stated by White
        (3, p.18):
      </IntextCitation>
    </p>
    <p style={{ marginLeft: 30 }}>
      Reference management plays a pivotal role in upholding scholarly integrity
      and avoiding plagiarism. By recognizing the contributions of original
      authors, you enhance your work&apos;s credibility and allow readers to
      track the sources you have consulted.
    </p>
    <p>
      With Vancouver&apos;s numeric system, citing in-text is straightforward
      and unobtrusive, making it an effective choice for many academic
      disciplines, especially within the scientific and medical fields.
    </p>
  </GuideSection>
);

const Bibliography: React.FC = () => (
  <GuideSection id="Biblography">
    <GuideHeader>Format Your Vancouver Bibliography</GuideHeader>
    <p>
      The reference list, often called a bibliography in the Vancouver style, is
      a crucial aspect of your scholarly work. It enumerates all the sources
      you&apos;ve cited in your paper, serving as a roadmap for your readers to
      validate and track your research materials.
    </p>
    <p>Here&apos;s your guide to crafting a Vancouver style reference list:</p>
    <p>
      <strong>Position and title:</strong> Begin the reference list on a
      separate page at the end of your document. The heading
      &quot;References&quot; (without quotes) should be centered at the top of
      the page. The heading should be in plain text, without any special
      formatting like bold, italics, or underlining.
    </p>
    <p>
      <strong>Spacing and indentation:</strong> Maintain consistent
      double-spacing throughout the reference list. Ensure a 1-inch margin on
      all sides of the page. Unlike Harvard style, Vancouver uses a flush left
      style, with no indentations for any lines in each citation.
    </p>
    <p>
      <strong>Sequence of citations:</strong> In the Vancouver style, entries
      are arranged in the numerical order they were cited in the text, not
      alphabetically. This system aligns with the numbered in-text citations,
      providing a clear reference path for readers.
    </p>
    <p>
      <strong>Citation formats:</strong> The Vancouver style provides specific
      formats for each source type, such as books, journal articles, online
      materials, etc. Each citation should include pertinent details like the
      authors&apos; names, title of the work, journal title (if applicable),
      year of publication, volume and issue numbers (if a journal), and page
      numbers.
    </p>
    <p>
      <strong>Consistent punctuation and case:</strong> Note that in Vancouver
      style, only the first word of an article or book title and any subtitle
      (and proper nouns) are capitalized. The rest of the title should be in
      lowercase.
    </p>
    <p>
      <strong>Accuracy check:</strong> As with any referencing style, it&apos;s
      crucial to ensure each citation&apos;s accuracy. Cross-verify each entry
      for correct spelling, punctuation, and format adherence to the Vancouver
      style rules.
    </p>
    <img
      src={require('images/vancouver-reference-list.png')}
      alt="Screenshot of a Vancouver reference list"
    />
    <p>
      By adhering to these guidelines, you can ensure that your Vancouver-style
      reference list is clear, consistent, and easy to navigate.
    </p>
  </GuideSection>
);

const BookCitations: React.FC = () => (
  <GuideSection id="Book">
    <GuideHeader>Cite Books in Vancouver</GuideHeader>
    <p>
      Mastering the Vancouver style can feel a bit like learning a new language,
      but don&apos;t worry, we&apos;re here to guide you through its alphabet
      and grammar. Our first topic is citing books in Vancouver style. It&apos;s
      an essential aspect of referencing as books continue to serve as a rich
      source of information for any researcher.
    </p>
    <p>
      Here&apos;s the general structure for citing a book in Vancouver style:
    </p>
    <CitationBlock header="Citing a book in Vancouver">
      Authors&apos; Last names and Initials. Title of book. Edition (if not the
      first). Place of publication: Publisher; Year of publication.
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Smith JD, Johnson KL. The intricacies of heart diseases. 2nd ed. New York:
      Harper & Collins; 2019.
    </CitationExampleBox>
    <p>
      <strong>Multiple authors:</strong> Include up to six authors in the
      citation, using the same format. If there are more than six authors, list
      the first six followed by &apos;et al.&apos; For example:
    </p>
    <CitationExampleBox>
      <TextWithColor>
        Smith JD, Johnson KL, Brown MM, Anderson RF, Clark KJ, Kim L, et al
      </TextWithColor>
      . The intricacies of heart diseases. 2nd ed. New York: Harper & Collins;
      2019.
    </CitationExampleBox>
    <p>
      <strong>No author:</strong> If no author is listed, begin the citation
      with the book title.
    </p>
    <CitationExampleBox>
      <TextWithColor>The intricacies of heart diseases.</TextWithColor> 2nd ed.
      New York: Harper & Collins; 2019.
    </CitationExampleBox>
    <p>
      <strong>Multi-volume book:</strong> When citing a specific volume of the
      book in Vancouver, make sure to include the abbreviation “Vol.” after the
      book title:
    </p>
    <CitationExampleBox>
      Smith J, Johnson L. The Encyclopedia of Science.{' '}
      <TextWithColor>Vol. 3</TextWithColor>. New York: Science Publishers; 2020.
    </CitationExampleBox>
    <p>
      <strong>Book edition:</strong> When citing books with multiple editions in
      the Vancouver style, you need to specify the edition number using the
      abbreviation “ed.” after the title:
    </p>
    <CitationExampleBox>
      Doe J. Advanced Chemistry. <TextWithColor>3rd ed</TextWithColor>. New
      York: Chemistry Press; 2023
    </CitationExampleBox>
    <p>
      <strong>Edited book:</strong> Citing an edited book in the Vancouver style
      requires a slightly different format to highlight the editor(s) rather
      than the author(s):
    </p>
    <CitationExampleBox>
      <TextWithColor>Johnson S, Kim T, editors.</TextWithColor> Foundations of
      Physics. New York: Physics Press; 2023.
    </CitationExampleBox>
    <p>
      <strong>Organization as author:</strong> If an organization is the author
      of your source, use the organization&apos;s name as the author.
    </p>
    <CitationExampleBox>
      <TextWithColor>American Heart Association</TextWithColor>. The intricacies
      of heart diseases. 2nd ed. New York: Harper & Collins; 2019.
    </CitationExampleBox>
    <p>
      Mastering these variations will ensure your book references in Vancouver
      style are spot on, enhancing the credibility of your work.
    </p>
  </GuideSection>
);

const JournalCitations: React.FC = () => (
  <GuideSection id="Journal">
    <GuideHeader>Cite Journal Articles in Vancouver</GuideHeader>
    <p>
      In this chapter, we are focusing on journal articles, a vital source of
      information, particularly in the medical and scientific fields. Here, we
      will teach you the basics, nuances, and variations to help you cite
      journal articles with ease.
    </p>
    <p>
      The standard structure of a journal article citation in Vancouver style
      is:
    </p>
    <CitationBlock header="Citing a journal article in Vancouver">
      Authors&apos; Last names and Initials. Title of the article. Abbreviated
      title of the journal. Year; Volume(Issue): Page numbers.
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Smith JD, Johnson KL. A novel approach to congenital heart diseases. J
      Cardiol. 2020; 34(2): 115-128.
    </CitationExampleBox>
    <p>
      <strong>No author:</strong> If no author is listed, the title of the
      article becomes the first element in the Vancouver citation.
    </p>
    <CitationExampleBox>
      <TextWithColor>
        A novel approach to congenital heart diseases
      </TextWithColor>
      . J Cardiol. 2020; 34(2): 115-128.
    </CitationExampleBox>
    <p>
      <strong>Online journals:</strong> If a journal is available online and the
      article has a DOI (Digital Object Identifier), include the DOI at the end
      of the citation.
    </p>
    <CitationExampleBox>
      Smith JD, Johnson KL. Exploring coronary heart diseases. J Cardiol Res.
      2019; 8(3):123-30.{' '}
      <TextWithColor>doi:10.1016/j.jcr.2019.03.003</TextWithColor>.
    </CitationExampleBox>
    <p>
      <strong>Journal without volume or issue numbers:</strong> In such cases,
      simply leave out the volume and issue details.
    </p>
    <CitationExampleBox>
      Smith JD, Johnson KL. Exploring coronary heart diseases. J Cardiol Res.
      2019:123-30.
    </CitationExampleBox>
    <p>
      With this guide in your reference toolbox, accurately citing journal
      articles in Vancouver style should be a breeze. As always, attention to
      detail is paramount in the world of citation!
    </p>
  </GuideSection>
);

const WebsiteCitations: React.FC = () => (
  <GuideSection id="Website">
    <GuideHeader>Cite Websites in Vancouver</GuideHeader>
    <p>
      Buckle up for another exciting expedition into the land of Vancouver
      citations. This time, our destination is the world wide web, a vast and
      varied landscape that&apos;s rich with information. Here&apos;s how to
      properly cite a website in the Vancouver style:
    </p>
    <CitationBlock header="Citing a website in Vancouver">
      Author(s) or organization. Title of webpage/document. Name of website (if
      different). Publication date (if available). Updated date (if available).
      Cited Date. Available from: URL.
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      World Health Organization. COVID-19 dashboard. World Health Organization;
      c2020. Cited 2023 May 20. Available from: https://covid19.who.int.
    </CitationExampleBox>
    <p>
      <strong>No author:</strong> If an online source has no identifiable
      author, begin your Vancouver citation with the title of the webpage.
    </p>
    <CitationExampleBox>
      <TextWithColor>COVID-19 dashboard</TextWithColor>. World Health
      Organization; c2020. Cited 2023 May 20. Available from:
      https://covid19.who.int.
    </CitationExampleBox>
    <p>
      <strong>No publication date:</strong> In cases when an online source has
      no publication date, use the abbreviation &quot;n.d.&quot; (no date). and
      Include the date you accessed the website.
    </p>
    <CitationExampleBox>
      American Heart Association. Cardiovascular Disease Statistics. Dallas:
      AHA; <TextWithColor>n.d. [cited 2023 May 20]</TextWithColor>. Available
      from:
      https://www.heart.org/en/health-topics/consumer-healthcare/what-is-cardiovascular-disease/understand.
    </CitationExampleBox>
    <p>
      <strong>No place or publisher:</strong> If no place of publication or
      publisher is identifiable, use the term &quot;s.n.&quot; (sine nomine),
      which means &quot;without a name&quot; in Latin.
    </p>
    <CitationExampleBox>
      Harvard Health. The truth about fats: the good, the bad, and the
      in-between. <TextWithColor>s.l: s.n;</TextWithColor> 2017 [cited 2023 May
      20]. Available from:
      www.health.harvard.edu/staying-healthy/the-truth-about-fats-bad-and-good
    </CitationExampleBox>
    <p>
      Whether it&apos;s the latest research findings or health advisories,
      websites provide crucial information for our papers. Remember these rules
      while citing websites in Vancouver style, and you&apos;ll ensure your
      references are as clear and accurate as your research!
    </p>
  </GuideSection>
);

const BookChapterCitations: React.FC = () => (
  <GuideSection id="BookChapter">
    <GuideHeader>Cite Book Chapters in Vancouver</GuideHeader>
    <p>
      In the world of academic writing, it&apos;s often necessary to dive deep
      and pinpoint specific sections of a larger work. That&apos;s where citing
      a book chapter in the Vancouver style comes in. This method allows you to
      directly attribute ideas to a certain chapter within a larger body of
      work, enhancing clarity and precision.
    </p>
    <p>
      To cite a chapter from a book in the Vancouver style, use the following
      structure:
    </p>
    <CitationBlock header="Citing a book chapter in Vancouver">
      Chapter author(s). Chapter title. In: Editor(s), editor(s). Title of book.
      Edition. Place of publication: Publisher; Year of publication. Page range.
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Rodriguez P. The role of time in quantum physics. In: Wright T, Lee H,
      editors. Theoretical Physics and Time Travel. 3rd ed. San Francisco: Time
      Travel Press; 2024. p. 47-72.
    </CitationExampleBox>
    <p>
      When the book <strong>has no editor</strong> or is{' '}
      <strong>written by the same author(s)</strong>, the structure changes
      slightly:
    </p>
    <CitationExampleBox>
      Hawking S. The Uncertainty Principle. In: A Brief History of Time. New
      York: Bantam Books; 1988. p. 54-60.
    </CitationExampleBox>
    <p>
      Remember that the authors&apos; names should be written as Last name First
      initial, with a comma separating different authors. The editor(s) of the
      book should be mentioned in the &quot;In:&quot; part of the citation.
      Edition and page range are essential components as well.
    </p>
  </GuideSection>
);

const NewspaperAndMagazineCitations: React.FC = () => (
  <GuideSection id="NewspaperAndMagazineCitations">
    <GuideHeader>Cite Newspapers & Magazine Articles in Vancouver</GuideHeader>
    <p>
      In the world of academic writing, it&apos;s often necessary to dive deep
      and pinpoint specific sections of a larger work. That&apos;s where citing
      a book chapter in the Vancouver style comes in. This method allows you to
      directly attribute ideas to a certain chapter within a larger body of
      work, enhancing clarity and precision.
    </p>
    <p>
      To cite a chapter from a book in the Vancouver style, use the following
      structure:
    </p>
    <CitationBlock header="Citing a newspaper article in Vancouver">
      Author(s). Article title. Newspaper title. Date of publication; Section
      (if given): beginning page of article (column number).
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Hamilton L. Mars colonization plans reveal new prospects for humanity. The
      Daily Planet. 2023 Jul 23; Space Exploration:6 (col 3).
    </CitationExampleBox>
    <p>
      For a magazine article, your citation will follow a slightly different
      format:
    </p>
    <p>
      <strong>Cite a magazine article in Vancouver:</strong>
    </p>
    <CitationBlockMini>
      Author(s). Article title. Magazine title. Year Month Day;
      Volume(Issue):Page numbers.
    </CitationBlockMini>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Smith J. The rise of artificial intelligence. Science Today. 2023
      Jun;12(6): 34-37.
    </CitationExampleBox>
    <p>
      Note that when citing <strong>online newspapers or magazines</strong>, you
      may need to include a URL and the date you accessed the information if the
      article is not available in print or database form:
    </p>
    <CitationExampleBox>
      Johnson R. Exploring the depths of the ocean. The Oceanic Times
      [Internet]. 2023 Mar 30 [cited 2023 Apr 5]; 8(9):22-25.{' '}
      <TextWithColor>
        Available from: http://www.oceanictimes.com/exploring-depths-ocean
      </TextWithColor>
    </CitationExampleBox>
    <p>
      Remember, in Vancouver style, each source is assigned a number that
      correlates with the order in which it appears in the text. When you
      reference a source, you&apos;ll use the corresponding number in your
      in-text citations.
    </p>
  </GuideSection>
);

const ConferencePaperCitations: React.FC = () => (
  <GuideSection id="ConferencePaperCitations">
    <GuideHeader>Cite Conference Papers in Vancouver</GuideHeader>
    <p>
      In scholarly writing, citing conference papers can help demonstrate that
      you&apos;re up-to-date with the latest discussions in your field. The
      Vancouver citation style provides a specific structure to follow when
      referencing these sources.
    </p>
    <p>The general format for a conference paper is:</p>
    <CitationBlock header="Citing a conference paper in Vancouver">
      Author(s). Title of paper. In: Editor(s), if given. Title of Conference;
      Date of Conference; Place of Conference. Place of publication: Publisher;
      Date of publication. Page numbers.
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Miller R, Lee S. Virtual reality in education: exploring new frontiers.
      In: Johnson T, editor. 5th International Conference on Educational
      Technology; 2023 Apr 10-12; San Francisco, USA. New York: TechEd
      Publications; 2023. p. 111-118.
    </CitationExampleBox>
    <p>
      If the proceedings are published in a journal, you can cite them as a
      journal article.
    </p>
    <p>
      <strong>An online conference paper citation</strong> would also require
      the URL and the date of access, for instance:
    </p>
    <CitationExampleBox>
      Davies M. The influence of social media on political campaigns. In: Walker
      P, editor. International Conference on Media and Politics; 2023 Feb 14-16;
      London, UK [Internet]. London: Media Studies Press; 2023{' '}
      <TextWithColor>[cited 2023 May 10]</TextWithColor>. p. 77-85.{' '}
      <TextWithColor>
        Available from: http://www.mediastudiespress.com/conference
      </TextWithColor>
    </CitationExampleBox>
    <p>
      Remember, in the Vancouver citation style, each source you cite will have
      a number assigned in the order of appearance in your work. This number is
      used for in-text citations. Be sure to double-check your references for
      accuracy and completeness!
    </p>
  </GuideSection>
);

const SocialMediaCitations: React.FC = () => (
  <GuideSection id="SocialMedia">
    <GuideHeader>Cite Social Media Posts in Vancouver</GuideHeader>
    <p>
      In the modern world, information and insights from social media are
      increasingly used in academic research, and thus it&apos;s important to
      understand how to properly cite them using the Vancouver style.
    </p>
    <p>
      The general format for citing a social media post in Vancouver style is:
    </p>
    <CitationBlock header="Citing a social media post in Vancouver">
      Author(s). Title of Message. Site Name. Date of post. Available from: URL
    </CitationBlock>
    <p>
      <strong>Example of a Vancouver citation for a Twitter post:</strong>
    </p>
    <CitationExampleBox>
      Gates B. Reading helped me to learn about the world and think in new
      ways...<TextWithColor>Twitter</TextWithColor>. 2023 May 1. Available from:
      https://twitter.com/BillGates/status/1234567890
    </CitationExampleBox>
    <p>
      If you&apos;d like to create a Vancouver{' '}
      <strong>reference for a Facebook post</strong>, you can follow a similar
      citation structure. Just remember to change the platform name!
    </p>
    <CitationExampleBox>
      NASA. We&apos;re inviting students around the world to help us name...
      <TextWithColor>Facebook</TextWithColor>. 2023 Apr 12. Available from:
      www.facebook.com/NASA/posts/1234567890
    </CitationExampleBox>
    <p>
      <strong>Example of a Vancouver citation for an Instagam post:</strong>
    </p>
    <CitationExampleBox>
      NatGeo. Photo by @jimrichardsonng | This is the Orkney Islands, a group of
      about 70 islands... <TextWithColor>Instagram</TextWithColor>. 2023 Mar 22.
      Available from: https://www.instagram.com/p/1234567890
    </CitationExampleBox>
    <p>
      When generating a Vancouver <strong>reference for a LinkedIn post</strong>
      , you should use the following citation structure:
    </p>
    <CitationExampleBox>
      Musk E. Just came back from visiting the Tesla Fremont factory...
      <TextWithColor>LinkedIn</TextWithColor>. 2023 Apr 18. Available from:
      https://www.linkedin.com/feed/update/urn:li:activity:1234567890
    </CitationExampleBox>
    <p>
      Please note that as the Vancouver style numbers citations in the order of
      appearance, be sure to number the in-text citation appropriately.
      Additionally, the access or citation date is usually not required in
      Vancouver style.
    </p>
    <p>
      But, for certain types of online content like social media posts, which
      can be edited or removed, it might be a good idea to include it. Always
      follow your institution&apos;s guidelines or consult your professor or a
      librarian.
    </p>
  </GuideSection>
);

const ThesisDissertationsCitations: React.FC = () => (
  <GuideSection id="ThesisDissertations">
    <GuideHeader>Cite Theses &amp; Dissertations in Vancouver</GuideHeader>
    <p>
      When it comes to citing a thesis or a dissertation in Vancouver style, the
      general format to follow is:
    </p>
    <CitationBlock header="Citing a thesis or dissertation in Vancouver">
      Author(s). Title [dissertation]. Place of publication: Publisher; Year.
    </CitationBlock>
    <p>For example:</p>
    <CitationExampleBox>
      Smith J. The Impact of Climate Change on Migration Patterns
      [dissertation]. Boston: Harvard University; 2020.
    </CitationExampleBox>
    <p>
      A Vancouver citation for{' '}
      <strong>a thesis or dissertation available online</strong> would also
      require the URL and the date of access. For instance:
    </p>
    <CitationExampleBox>
      Singer J. Analyzing Artificial Intelligence Applications in Healthcare
      [dissertation]. Los Angeles: University of Southern California; 2022.{' '}
      <TextWithColor>
        Available from: https://repository.usc.edu/dissertation/doe
      </TextWithColor>
    </CitationExampleBox>
    <p>
      If the thesis or dissertation has a DOI, you can replace the URL with DOI.
    </p>
    <p>
      Remember, all in-text citations should be numbered and correspond to the
      numbered reference in the reference list. Ensure that you keep track of
      your references as they appear in your text.
    </p>
  </GuideSection>
);

const DataCitations: React.FC = () => (
  <GuideSection id="DataCitations">
    <GuideHeader>Cite Data in Vancouver</GuideHeader>
    <p>
      Citing data sets and databases in the Vancouver style typically involves
      these components: author(s), title of data set/database, type of medium,
      publication place, publisher, year of publication, and availability, which
      often includes the URL or DOI.
    </p>
    <p>The general format is:</p>
    <CitationBlock header="Citing a dataset in Vancouver">
      Author(s). Title of data set/database [Internet]. Place of publication:
      Publisher; Year. Available from: URL or DOI
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Department of Health and Human Services (US). US Cancer Statistics:
      1999-2014, WONDER Online Database [Internet]. Atlanta: Department of
      Health and Human Services (US); 2017. Available from:
      https://wonder.cdc.gov/cancer.html
    </CitationExampleBox>
    <p>
      <strong>Things to keep in mind:</strong>
    </p>
    <SingleSpacedList>
      <li>
        When the author is the same as the publisher, you can drop the
        author&apos;s name.
      </li>
      <li>
        If a DOI (Digital Object Identifier) is provided, use it in place of the
        URL.
      </li>
      <li>
        For database content that changes over time (e.g., Wikis), include the
        date of the version or revision number along with the date you accessed
        the source.
      </li>
      <li>
        When the date of publication is not available, replace it with
        &apos;undated&apos;.
      </li>
    </SingleSpacedList>
    <p>
      Remember that in-text citations for Vancouver follow the numerical style.
      The reference list should be ordered numerically in the order references
      appear in your text. The numbers are placed in brackets or superscript.
    </p>
    <p>
      For example, the citation (1) refers to the first source listed in your
      reference list. Be consistent with your numbering and ensure each number
      corresponds to the right source.
    </p>
  </GuideSection>
);

const DictionariesAndEncyclopediaCitations: React.FC = () => (
  <GuideSection id="DictionariesAndEncyclopedias">
    <GuideHeader>Cite Dictionaries and Encyclopedias in Vancouver</GuideHeader>
    <p>
      Citing dictionaries and encyclopedias correctly is essential for academic
      and scientific work. In the Vancouver style, citing these reference works
      follows a similar structure to other citations but has a few specific
      rules. Let&apos;s explore these:
    </p>
    <CitationBlock header="Citing a dictionary or encyclopedia in Vancouver">
      Author/editor. Title of entry. In: Title of Book. Edition. Place of
      publication: Publisher; Year. Page(s).
    </CitationBlock>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Smith J, editor. Hematology. In: Encyclopedia of Medicine. 3rd ed. London:
      Health Press; 2020. p. 123-7.
    </CitationExampleBox>
    <p>
      <strong>Things to keep in mind:</strong>
    </p>
    <SingleSpacedList>
      <li>
        If the entry has no author or editor, the citation starts with the title
        of the entry.
      </li>
      <li>
        If it is an online dictionary or encyclopedia, include
        &apos;[Internet]&apos; after the title, as well as &apos;Available from:
        URL or DOI&apos; at the end.
      </li>
      <li>If no date is available, use &apos;undated&apos;.</li>
      <li>
        If there are multiple editions, use the most recent one available.
      </li>
    </SingleSpacedList>
    <p>
      Therefore, in the Vancouver format,{' '}
      <strong>an online dictionary entry without a known author</strong> would
      be:
    </p>
    <CitationExampleBox>
      <TextWithColor>Hematology</TextWithColor>. In: MedlinePlus Medical
      Encyclopedia [Internet]. Bethesda: National Library of Medicine (US);
      2021.{' '}
      <TextWithColor>
        Available from: https://medlineplus.gov/encyclopedia.html
      </TextWithColor>
    </CitationExampleBox>
    <p>
      Pro Tip: Keeping track of your references in Vancouver style can be
      challenging due to its numerical system. An effective practice is to
      maintain a separate document or file to update your reference list in
      real-time as you cite each source.
    </p>
    <p>
      That way, you can easily refer back to your list and ensure each citation
      matches the corresponding number in your reference list. This method also
      makes it easier to spot if you&apos;ve missed citing any source in your
      text. Remember, in Vancouver style, your sources must be listed in the
      order they appear in your text.
    </p>
  </GuideSection>
);

const DrugCitations: React.FC = () => (
  <GuideSection id="Drugs">
    <GuideHeader>Cite Drug Information in Vancouver</GuideHeader>
    <p>
      The medicine field often requires the citation of drug information sources
      for both academic and professional purposes. In Vancouver style, the
      citation format will slightly vary depending on the type of drug
      information source you are citing - this could be a drug monograph from a
      reference book or information from an online drug database.
    </p>
    <p>
      Below you will find a general citation structure for drug information:
    </p>
    <CitationBlock header="Citing a drug information source in Vancouver">
      Author(s). Title. In: Editor(s), editors. Book Title. Edition. Place of
      Publication: Publisher; Year. Page(s).
    </CitationBlock>
    <p>
      Now, let&apos;s imagine we&apos;re citing a drug monograph for paracetamol
      from a reference book called &quot;Drug Reference Guide&quot;, edited by
      J. Smith and published in New York by Pharma Publishers in 2022.
    </p>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Paracetamol. In: Smith J, editor. Drug Reference Guide. New York: Pharma
      Publishers; 2022. p. 250-253.
    </CitationExampleBox>
    <p>
      Or, we could be citing drug information for Paracetamol from an online
      database named &quot;eDrugInfo&quot; that was last updated in 2023.
    </p>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Paracetamol. eDrugInfo [Internet]. London: eDrugInfo Publishing; 2020
      [updated 2023 Apr 1; cited 2023 Apr 20]. Available from:
      www.edruginfo.com/drug/paracetamol
    </CitationExampleBox>
    <p>
      It&apos;s important to remember that when citing sources in Vancouver
      style, each source should be numbered in the order they appear in your
      text. Furthermore, remember to include the date when you last accessed the
      website as online contents can be updated or changed over time.
    </p>
    <p>
      <TextWithColor>Pro Tip:</TextWithColor> If the drug information
      you&apos;re citing is updated frequently online, you may want to take a
      screenshot or save a copy of the webpage for your records. This can serve
      as a reference in case the online content changes after you&apos;ve cited
      it.
    </p>
  </GuideSection>
);

const EvidenceSummaryCitations: React.FC = () => (
  <GuideSection id="EvidenceSummaries">
    <GuideHeader>Cite Evidence Summaries in Vancouver</GuideHeader>
    <p>
      Evidence summaries are an essential source of information in the field of
      healthcare and medical research. They provide a concise overview of the
      available evidence on a particular health-related issue. In the Vancouver
      citation style, evidence summaries can be cited much like other online
      sources, but there are specific details to consider.
    </p>
    <p>
      Below you will find a basic Vancouver citation structure for evidence
      summaries:
    </p>
    <CitationBlock header="Citing an evidence summary in Vancouver">
      Author(s). Title of summary. In: Source [Internet]. Place of publication:
      Publisher; Year of publication. [cited Year Month Day]. Available from:
      URL
    </CitationBlock>
    <p>
      Now, consider that you&apos;re citing an evidence summary titled
      &quot;Effectiveness of Flu Vaccination&quot;, authored by J. Doe and
      published on the World Health Organization&apos;s website in 2023.
    </p>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Doe J. Effectiveness of Flu Vaccination. In: World Health Organization
      [Internet]. Geneva: World Health Organization; 2023 [cited 2023 Apr 25].
      Available from: <u>https://www.who.int/flu/vaccination/effectiveness</u>
    </CitationExampleBox>
    <p>
      Alternatively, here&apos;s how you would cite an evidence summary titled
      &quot;Efficacy of Hand Sanitizers&quot;, published on the CDC&apos;s
      website in 2023, where no specific author is mentioned:
    </p>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Centers for Disease Control and Prevention. Efficacy of Hand Sanitizers.
      In: CDC [Internet]. Atlanta: CDC; 2023 [cited 2023 Apr 26]. Available
      from: https://www.cdc.gov/hand-sanitizers/efficacy
    </CitationExampleBox>
    <p>
      Please remember that online sources should include the date when the
      website was accessed as online content can change over time.
    </p>
    <p>
      <TextWithColor>Pro Tip:</TextWithColor> As best practice, ensure to check
      back to the online summary frequently to stay updated, as they often
      contain dynamic content that may change or be updated over time.
    </p>
  </GuideSection>
);

const StandardsAndPharmacopoeiaCitations: React.FC = () => (
  <GuideSection id="StandardsAndPharmacopoeia">
    <GuideHeader>Cite Standards & Pharmacopoeias in Vancouver</GuideHeader>
    <p>
      Standards and pharmacopoeias are vital resources for industries and
      researchers alike. Standards provide detailed specifications to ensure
      products, services, and systems are reliable and effective. Pharmacopoeias
      are official publications that contain directions for the identification
      of compound medicines. When using Vancouver style, these sources are
      typically referenced in a similar way to books.
    </p>
    <p>Here&apos;s the general citation structure for pharmacopoeias:</p>
    <CitationBlock header="Citing standards and pharmacopoeias in Vancouver">
      Standard/Pharmacopoeia Number: Year of Publication. Title. Place of
      Publication: Publisher; Year.
    </CitationBlock>
    <p>
      Imagine you&apos;re citing the ISO 9001:2015 standard, titled
      &quot;Quality Management Systems - Requirements&quot;:
    </p>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      ISO 9001:2015. Quality Management Systems - Requirements. Geneva:
      International Organization for Standardization; 2015.
    </CitationExampleBox>
    <p>
      Or, assume you&apos;re referencing the United States Pharmacopeia (USP)
      42-NF 37:
    </p>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      USP 42-NF 37: 2019. The United States Pharmacopeia and National Formulary.
      Rockville: The United States Pharmacopeial Convention; 2019.
    </CitationExampleBox>
    <p>
      For an online standard, like the ISO 14001:2015 titled &quot;Environmental
      Management Systems - Requirements&quot;, your Vancouver citation might
      look like:
    </p>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      ISO 14001:2015. Environmental Management Systems - Requirements
      [Internet]. Geneva: International Organization for Standardization; 2015
      [cited 2023 May 15]. Available from:
      <u>https://www.iso.org/standard/60857.html</u>
    </CitationExampleBox>
    <p>
      <TextWithColor>Pro Tip:</TextWithColor> Since standards and pharmacopoeias
      are frequently updated, always use the most recent edition in your
      research and cite it accordingly.
    </p>
  </GuideSection>
);

const TablesAndFiguresCitations: React.FC = () => (
  <GuideSection id="TablesAndFigures">
    <GuideHeader>Cite Tables & Figures in Vancouver</GuideHeader>
    <p>
      Tables and figures are valuable resources that provide visual
      representations of data and concepts. When using Vancouver style, citing
      tables and figures varies slightly depending on whether the table or
      figure is from a book, a journal article, a website, or is your own
      creation. Here, we&apos;ll go over these variations.
    </p>
    <p>For tables and figures, you&apos;ll usually include the following:</p>
    <CitationBlock header="Citing a table or a figure in Vancouver">
      Figure/Table Number: Author(s). Title of the article/book. In: Title of
      Journal/Book. Edition (if applicable). Place of publication: Publisher;
      Year of Publication. Page number(s).
    </CitationBlock>
    <p>
      If you&apos;re <strong>citing a figure from a journal article</strong>,
      your citation might look like this:
    </p>
    <CitationBlockMini>
      Fig. 1: Smith J, Doe M. Effects of light exposure on plant growth. In:
      Botany Today. 2020;32:224-235.
    </CitationBlockMini>
    <p>
      If you&apos;re <strong>citing a table from a book</strong>, your citation
      might look like this:
    </p>

    <CitationBlockMini>
      Table 1: James L. The Influence of Music on Cognitive Performance. In:
      Psychology of Music. 2nd ed. New York: Oxford University Press; 2022.
      p.56.
    </CitationBlockMini>
    <p>
      For <strong>a figure or table from a website</strong>, you may include the
      URL and access date:
    </p>
    <CitationBlockMini>
      Fig. 2: Johnson R. Global Warming Trends. In: Climate Studies [Internet].
      2023 [cited 2023 May 20]. Available from:
      http://www.climatestudies.com/figures
    </CitationBlockMini>
    <p>
      If you <strong>created the table or figure yourself</strong>, the citation
      may look like this:
    </p>
    <CitationBlockMini>Table 2: Created by author.</CitationBlockMini>
    <p>
      <TextWithColor>Pro Tip:</TextWithColor> Always remember to reference the
      source of the data presented in a figure or table, even if you created the
      visual yourself. Indicate in the caption if you have made modifications to
      the original.
    </p>
  </GuideSection>
);

const TechnicalReportsCitations: React.FC = () => (
  <GuideSection id="TechnicalReports">
    <GuideHeader>Cite Technical Reports in Vancouver</GuideHeader>
    <p>
      Government or technical reports are a trove of detailed information that
      can support your research or arguments. In Vancouver style, citing these
      kinds of reports is straightforward, but there are still a few key pieces
      of information you need to gather for a complete and accurate citation.
    </p>
    <p>
      Here&apos;s the basic structure for citing government or technical reports
      in Vancouver:
    </p>
    <CitationBlock header="Citing a government or technical report in Vancouver">
      Author(s). Title of Report. Place of publication: Publisher; Year. Report
      No.: report number.
    </CitationBlock>
    <p>
      The general rule of thumb in Vancouver citation is to treat the issuing
      agency as the author. Here&apos;s an example:
    </p>
    <CitationExampleBox>
      US Department of Health and Human Services. Youth tobacco surveillance.
      Atlanta: US Department of Health and Human Services, Centers for Disease
      Control and Prevention; 2000. Report No.: 00-0032.
    </CitationExampleBox>
    <p>
      If no author is identified, include the issuing agency as the corporate
      author.
    </p>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      <TextWithColor>World Health Organization</TextWithColor>. Global
      tuberculosis report 2020. Geneva: World Health Organization; 2020. Report
      No.: WHO/CDS/TB/2020.25.
    </CitationExampleBox>
    <p>If the report is retrieved online, add its URL and access date:</p>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      European Space Agency. Climate Change Initiative. Paris: European Space
      Agency; 2020. Report No.: ESA/SCI(2020)1. Available from:{' '}
      <TextWithColor>
        http://www.esa.int/ClimateChangeInitiative [Accessed 20th May 2023]
      </TextWithColor>
      .
    </CitationExampleBox>
    <p>
      <TextWithColor>Pro Tip:</TextWithColor> Vancouver citations typically
      don&apos;t include access dates for print materials, but it&apos;s
      recommended to include them for online resources due to the changing
      nature of the web. This helps future readers verify the resource&apos;s
      availability.
    </p>
  </GuideSection>
);

const CourseMaterialCitations: React.FC = () => (
  <GuideSection id="CourseMaterials">
    <GuideHeader>Cite Course Materials & Lectures in Vancouver</GuideHeader>
    <p>
      Course materials such as lecture notes, slides, and handouts are vital
      educational resources in academic settings. When used in your research,
      it&apos;s important to accurately cite these materials in the Vancouver
      style to give credit to the educators who developed them.
    </p>
    <p>
      The basic structure for citing course materials in Vancouver style is as
      follows:
    </p>
    <CitationBlock header="Citing a course material in Vancouver">
      Author(s). Title of material. City: University or College; Year. Available
      from: URL (if applicable)
    </CitationBlock>
    <p>
      <strong>Citing lecture notes in Vancouver:</strong>
    </p>
    <CitationExampleBox>
      Johnson A. Anatomy and Physiology: Lecture 5, The Nervous System. New
      York: Columbia University; 2021.
    </CitationExampleBox>
    <p>
      When citing materials from an online course, the citation should include
      the URL and the date you accessed the material.
    </p>
    <p>
      <strong>For example:</strong>
    </p>
    <CitationExampleBox>
      Smith J. Foundations of Physics: Lecture 2, Laws of Motion. Cambridge: MIT
      OpenCourseWare; 2023. Available from:
      <TextWithColor>
        http://ocw.mit.edu/foundations-of-physics/lecture-2 [Accessed 20th May
        2023]
      </TextWithColor>
      .
    </CitationExampleBox>
    <p>
      <TextWithColor>Pro Tip:</TextWithColor> Course materials often come with
      unique identifiers such as module or lecture numbers. Including these in
      your citation can make it easier for your readers to locate the specific
      material you referenced.
    </p>
  </GuideSection>
);

const PersonalCommunicationsCitations: React.FC = () => (
  <GuideSection id="PersonalCommunications">
    <GuideHeader>Cite Personal Communications in Vancouver</GuideHeader>
    <p>
      Personal communications are sources that are not publicly accessible and
      often include conversations, interviews, emails, or other correspondence.
      The Vancouver citation style has a specific way of citing such references
      that are not accessible to the reader.
    </p>
    <p>
      In Vancouver style, personal communications should be cited within the
      text rather than in the reference list. You should include the
      communicator&apos;s name, their credentials, the fact that it is a
      personal communication, and the date of communication.
    </p>
    <p>
      Here&apos;s how you may cite a personal communication, such as private
      conversation:
    </p>
    <p>
      Dr. Susan Thompson{' '}
      <IntextCitation>
        (Professor of Sociology, Yale University, New Haven, USA, personal
        communication, May 20, 2023)
      </IntextCitation>{' '}
      noted that...
    </p>
    <p>
      When citing an email, it should be treated as a personal communication and
      cited in the same way:
    </p>
    <p>
      As stated by J. K. Rowling{' '}
      <IntextCitation>
        (Author, Edinburgh, UK, personal communication, via email, May 20, 2023)
      </IntextCitation>
      ...
    </p>
    <p>
      <TextWithColor>Pro Tip:</TextWithColor> You must obtain permission from
      the person you&apos;re citing before including their personal
      communication in your work. This not only respects their privacy but also
      verifies the accuracy of your information.
    </p>
    <p>
      Remember, because personal communications are not retrievable by others,
      they are typically not included in the reference list in Vancouver style.
    </p>
  </GuideSection>
);

const FinalNotes: React.FC = () => (
  <GuideSection id="FinalNotes">
    <GuideHeader>Vancouver Style: Final Notes</GuideHeader>

    <p>
      In this guide, we&apos;ve provided an overview of citing various types of
      sources in Vancouver style, with practical examples and the occasional pro
      tip. We&apos;ve unveiled the mysteries of this numeric referencing system,
      illustrating how each source is identified by a unique number that
      corresponds to its appearance in the text.
    </p>
    <p>
      This systematic and orderly approach makes the Vancouver style an ideal
      choice for medical and scientific papers. It provides a clear, unobtrusive
      way to refer to sources within the text while maintaining a comprehensive
      list of references at the end.
    </p>
    <p>
      As always, our team at Bibcitation is ready to assist you with our
      efficient citation generator. For any doubts or queries, you can refer
      back to this comprehensive guide or reach out to us for further
      assistance.
    </p>
    <p>
      Here&apos;s to mastering the art of citation and elevating your academic
      writing!
    </p>
  </GuideSection>
);

export default VancouverGuide;
